<template>
  <div class="criteria-view-edit-in-place">
    <!-- Header row: label + action buttons -->
    <div
      class="criteria-view-edit-in-place__header"
      :class="{ 'criteria-view-edit-in-place__header--editable': isEditable }"
    >
      <span
        class="criteria-view-edit-in-place__label"
        :class="{ 'criteria-view-edit-in-place__label--editable': isEditable }"
      >
        {{ isEditable ? "Criteria*" : "Criteria" }}
      </span>

      <div v-if="!controlledByCheckpoint" class="criteria-view-edit-in-place__actions">
        <template v-if="isDirty">
          <StandardButton
            type="magenta"
            variant="text-secondary"
            size="small"
            label="Discard changes"
            leftIcon="cornerUpLeft"
            class="discard-btn"
            @click="$emit('discard-changes')"
          />
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Save Changes"
            @click="$emit('save-changes')"
          />
        </template>
        <template v-else-if="isEditable">
          <StandardButton
            type="magenta"
            variant="tertiary"
            size="small"
            label="Cancel Edit"
            class="cancel-edit-btn"
            @click="$emit('cancel-edit')"
          />
        </template>
        <template v-else>
          <StandardButton
            type="magenta"
            variant="tertiary"
            size="small"
            label="Edit"
            class="edit-btn"
            @click="$emit('edit')"
          />
        </template>
      </div>
    </div>

    <!-- Criteria input -->
    <div class="criteria-view-edit-in-place__input checkpoint">
      <div
        v-if="!isEditable"
        class="criteria-view-edit-in-place__readonly-field"
        data-testid="criteria-readonly-field"
      >
        <span class="criteria-view-edit-in-place__readonly-prefix">{{ criteriaPrefix }}</span>
        <span class="criteria-view-edit-in-place__readonly-criteria">{{ criteria }}</span>
      </div>
      <ExTextInput
        v-else
        aria-label="Criteria"
        placeholder="Add criteria"
        :value="criteria"
        :required="isEditable"
        dropdownSide="left"
        :dropdownItems="dropdownItems"
        :class="[
          'add-criteria',
          {
            'input-dirty': isInputDirty,
            'prefix-dirty': isPrefixDirty,
          },
        ]"
        :showCustomIcon="showTrailingIcon"
        :selectedItem="criteriaPrefix"
        :editModal="true"
        @dropdown-input="$emit('update:criteriaPrefix', $event)"
        @input="$emit('update:criteria', $event)"
        @onBlur="handleCriteriaBlur"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { maybeNormalizeCriteriaBodyEn } from "@components/utils/normalizeCriteriaBodyEn";
import store from "@/store";

const props = defineProps({
  criteria: { type: String, default: "" },
  criteriaPrefix: { type: String, default: "Can" },
  dropdownItems: { type: Array, default: () => ["Can", "Does", "Demonstrated", "Proved"] },
  controlledByCheckpoint: { type: Boolean, default: true },
  readonly: { type: Boolean, default: true },
  showTrailingIcon: { type: Boolean, default: false },
});

const emit = defineEmits([
  "edit",
  "cancel-edit",
  "save-changes",
  "discard-changes",
  "update:criteria",
  "update:criteriaPrefix",
]);

const ideaMDP452 = computed(() => Boolean(store.getters["featureFlags/ideaMDP452"]));

function handleCriteriaBlur() {
  const normalized = maybeNormalizeCriteriaBodyEn(props.criteria, ideaMDP452.value);
  if (normalized !== props.criteria) {
    emit("update:criteria", normalized);
  }
}

const initialCriteria = ref(props.criteria);
const initialCriteriaPrefix = ref(props.criteriaPrefix);

watch(
  () => props.readonly,
  (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
      initialCriteria.value = props.criteria;
      initialCriteriaPrefix.value = props.criteriaPrefix;
    }
    if (newVal === true) {
      initialCriteria.value = props.criteria;
      initialCriteriaPrefix.value = props.criteriaPrefix;
    }
  }
);

// Keep viewer baseline in sync when parent pushes new server criteria (e.g. after Reload).
watch(
  () => [props.criteria, props.criteriaPrefix],
  ([criteria, prefix]) => {
    if (!props.readonly) return;
    initialCriteria.value = criteria;
    initialCriteriaPrefix.value = prefix;
  }
);

const isInputDirty = computed(() => props.criteria !== initialCriteria.value);
const isPrefixDirty = computed(() => props.criteriaPrefix !== initialCriteriaPrefix.value);
const isDirty = computed(() => isInputDirty.value || isPrefixDirty.value);
// controlledByCheckpoint: parent owns edit mode via `readonly` — never show dirty/edit UI to viewers.
const isEditable = computed(() => {
  if (props.controlledByCheckpoint) return !props.readonly;
  return !props.readonly || isDirty.value;
});
</script>

<style scoped>
.criteria-view-edit-in-place__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.criteria-view-edit-in-place__header--editable {
  align-items: center;
  gap: 6px;
}

.criteria-view-edit-in-place__label {
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #344054;
  flex: 1;
}

.criteria-view-edit-in-place__label--editable {
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  align-items: center;
  height: 36px;
}

.criteria-view-edit-in-place__actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.criteria-view-edit-in-place__readonly-field {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 8px 14px;
  background-color: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: #344054;
}

.criteria-view-edit-in-place__readonly-prefix {
  flex-shrink: 0;
  margin-right: 6px;
  font-weight: 400;
  opacity: 0.8;
}

.criteria-view-edit-in-place__readonly-criteria {
  flex: 1;
  font-weight: 400;
  opacity: 0.8;
}

/* Remove default ExTextInput top padding */
.checkpoint ::v-deep .v-text-field {
  padding-top: 0px !important; /* overrides Vuetify v-text-field default 20px top padding */
}

/* Prepend widths — override Vuetify's default prepend-inner margins to align prefix dropdown */
::v-deep .add-criteria .left-can-prepand {
  min-width: 65px;
  top: -3px; /* centers the prefix chip against the input row (verified against Figma node 42376:26416) */
  margin-top: 0 !important; /* overrides Vuetify prepend-inner margin that offsets the dropdown */
  padding-top: 0 !important; /* overrides Vuetify prepend-inner padding that offsets the dropdown */
}
::v-deep .add-criteria .left-does-prepand {
  min-width: 72px;
  top: -3px; /* centers the prefix chip against the input row (verified against Figma node 42376:26416) */
  margin-top: 0 !important; /* overrides Vuetify prepend-inner margin that offsets the dropdown */
  padding-top: 0 !important; /* overrides Vuetify prepend-inner padding that offsets the dropdown */
}
::v-deep .add-criteria .left-demonstrated-prepand {
  min-width: 132px;
  top: -3px; /* centers the prefix chip against the input row (verified against Figma node 42376:26416) */
  margin-top: 0 !important; /* overrides Vuetify prepend-inner margin that offsets the dropdown */
  padding-top: 0 !important; /* overrides Vuetify prepend-inner padding that offsets the dropdown */
}
::v-deep .add-criteria .left-proved-prepand {
  min-width: 80px;
  top: -3px; /* centers the prefix chip against the input row (verified against Figma node 42376:26416) */
  margin-top: 0 !important; /* overrides Vuetify prepend-inner margin that offsets the dropdown */
  padding-top: 0 !important; /* overrides Vuetify prepend-inner padding that offsets the dropdown */
}
::v-deep
  .add-criteria.ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  label.v-label--active {
  margin-left: -60px !important; /* overrides Vuetify active-label position which doesn't account for wide prepend */
}
::v-deep
  .add-criteria.ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  label.v-label--active {
  margin-left: -10px !important; /* overrides Vuetify active-label position which doesn't account for wide prepend */
}

/* Dropdown z-index — prevent prefix dropdown from being clipped by parent overflow:hidden */
.checkpoint {
  position: relative;
  z-index: 1;
  overflow: visible !important; /* overrides parent container overflow:hidden that clips the dropdown */
}
::v-deep .add-criteria {
  overflow: visible !important; /* overrides Vuetify scoped overflow that clips the prefix dropdown */
}
::v-deep .add-criteria .code-select-wrapper {
  overflow: visible !important; /* overrides Vuetify scoped overflow that clips the prefix dropdown */
}
::v-deep .add-criteria .v-menu__content {
  z-index: 9999 !important; /* overrides Vuetify default z-index so dropdown renders above modal overlays */
  position: absolute !important; /* overrides Vuetify fixed positioning that misaligns dropdown in modals */
}
::v-deep .add-criteria .v-select__content {
  z-index: 9999 !important; /* overrides Vuetify default z-index so dropdown renders above modal overlays */
}
::v-deep .add-criteria .v-list {
  z-index: 9999 !important; /* overrides Vuetify default z-index so dropdown renders above modal overlays */
}

/* Criteria-only dirty: purple on text area */
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-can-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset,
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-does-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    white 0%,
    white 71px,
    #d9d6fe 71px,
    #d9d6fe 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-can-prepand
  .v-text-field
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-does-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-demonstrated-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    white 0%,
    white 143px,
    #d9d6fe 143px,
    #d9d6fe 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-proved-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    white 0%,
    white 91px,
    #d9d6fe 91px,
    #d9d6fe 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.input-dirty:not(.prefix-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}

/* Prefix-only dirty: purple on prefix dropdown */
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden;
  background: linear-gradient(
    to right,
    #d9d6fe 0%,
    #d9d6fe 79px,
    white 65px,
    white 100%
  ) !important;
  z-index: 0 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-text-field
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .v-text-field
  .v-input__control
  .v-input__slot {
  background: transparent !important;
  position: relative;
  z-index: 1 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important;
  z-index: 2 !important;
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .code-select-wrapper,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .code-select-wrapper,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .level-picker
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .level-picker
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .level-picker
  .v-input__control
  fieldset,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .level-picker
  .v-input__control
  fieldset,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-can-prepand
  .v-select__selections,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-does-prepand
  .v-select__selections {
  background: transparent !important;
  position: relative; /* overrides Vuetify scoped style that sets a background on input fields */
  z-index: 1 !important; /* overrides Vuetify scoped style that sets a background on input fields */
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden; /* overrides Vuetify scoped style that sets a background on input fields */
  background: linear-gradient(
    to right,
    #d9d6fe 0%,
    #d9d6fe 147px,
    white 137px,
    white 100%
  ) !important;
  z-index: 0 !important; /* overrides Vuetify scoped style that sets a background on input fields */
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .code-select-wrapper,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .level-picker
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .level-picker
  .v-input__control
  fieldset,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-select__selections {
  background: transparent !important; /* overrides Vuetify scoped style that sets a background on input fields */
  position: relative; /* overrides Vuetify scoped style that sets a background on input fields */
  z-index: 1 !important; /* overrides Vuetify scoped style that sets a background on input fields */
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-demonstrated-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  z-index: 2 !important; /* overrides Vuetify scoped style that sets a background on input fields */
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field--outlined
  .v-input__control
  fieldset {
  overflow: hidden; /* overrides Vuetify scoped style that sets a background on input fields */
  background: linear-gradient(
    to right,
    #d9d6fe 0%,
    #d9d6fe 95px,
    white 85px,
    white 100%
  ) !important;
  z-index: 0 !important; /* overrides Vuetify scoped style that sets a background on input fields */
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .code-select-wrapper,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .level-picker
  .v-input__control
  .v-input__slot,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .level-picker
  .v-input__control
  fieldset,
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-select__selections {
  background: transparent !important; /* overrides Vuetify scoped style that sets a background on input fields */
  position: relative; /* overrides Vuetify scoped style that sets a background on input fields */
  z-index: 1 !important; /* overrides Vuetify scoped style that sets a background on input fields */
}
::v-deep
  .add-criteria.prefix-dirty:not(.input-dirty).ex_text_input-proved-prepand
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  z-index: 2 !important; /* overrides Vuetify scoped style that sets a background on input fields */
}

/* Both dirty: full purple background */
::v-deep .add-criteria.prefix-dirty.input-dirty .v-text-field--outlined .v-input__control fieldset {
  overflow: hidden; /* overrides Vuetify scoped style that sets a background on input fields */
  background-color: #d9d6fe !important; /* overrides Vuetify scoped style that sets a background on input fields */
  z-index: 0 !important; /* overrides Vuetify scoped style that sets a background on input fields */
}
::v-deep .add-criteria.prefix-dirty.input-dirty .v-text-field .v-input__control .v-input__slot {
  background: transparent !important; /* overrides Vuetify scoped style that sets a background on input fields */
  position: relative; /* overrides Vuetify scoped style that sets a background on input fields */
  z-index: 1 !important; /* overrides Vuetify scoped style that sets a background on input fields */
}
::v-deep
  .add-criteria.prefix-dirty.input-dirty
  .v-text-field
  .v-input__control
  .v-input__slot
  .v-input__prepend-inner {
  background: transparent !important; /* overrides Vuetify scoped style that sets a background on input fields */
  z-index: 2 !important; /* overrides Vuetify scoped style that sets a background on input fields */
}
::v-deep .add-criteria.prefix-dirty.input-dirty .code-select-wrapper,
::v-deep .add-criteria.prefix-dirty.input-dirty .level-picker .v-input__control .v-input__slot,
::v-deep .add-criteria.prefix-dirty.input-dirty .level-picker .v-input__control fieldset,
::v-deep .add-criteria.prefix-dirty.input-dirty .v-select__selections {
  background: transparent !important; /* overrides Vuetify scoped style that sets a background on input fields */
}
::v-deep .add-criteria.prefix-dirty .v-text-field .v-input__control .v-input__slot input {
  background-color: transparent !important; /* overrides Vuetify scoped style that sets a background on input fields */
}
::v-deep .cancel-edit-btn,
::v-deep .edit-btn {
  background-color: transparent !important; /* overrides Vuetify scoped style that sets a background on secondary action buttons */
}
</style>
